import { Component, Injectable, OnDestroy, OnInit, inject } from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";
import { lastValueFrom, debounceTime, distinctUntilChanged, Subject } from "rxjs";
import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogProgressBarComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ConfigurationManagerService, ConnectionService, ScreenOrientationService } from "src/app/core";
import {
    LeanPMSApiService,
    LeanPMSGroupReservation,
    LeanPMSHotel,
    LeanPMSReservation,
    LeanPMSReservationExtra
} from 'src/app/features/digital-payments';
import { DevLogger } from 'src/app/shared/dev-logger';
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { CommonModule } from "@angular/common";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatTabChangeEvent, MatTabsModule } from "@angular/material/tabs";
import { FormsModule } from "@angular/forms";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { SharedModule } from "src/app/shared/shared.module";
import { MatListModule, MatSelectionListChange } from "@angular/material/list";
import { MatDividerModule } from "@angular/material/divider";
import { BarcodeComponent } from "src/app/shared/components";
import { MathUtils } from "@tilby/tilby-ui-lib/utilities";
import { asyncTimeout } from "src/app/shared/utils";

export type LeanPMSReferenceData = { reservation: number, hotel: number } | { reservation_group: number, hotel: number };

export type LeanPMSPaymentsReturn = {
    referenceData: LeanPMSReferenceData,
    reservation: LeanPMSReservation | LeanPMSGroupReservation
};

export type LeanPMSPaymentsDialogOptions = {
    amount?: number,
    enableConfirm?: boolean,
    reservationId?: number,
    paymentMethodId?: number
}

export type LeanPmsPaymentsDialogData = { options: LeanPMSPaymentsDialogOptions };

@Component({
    selector: "app-lean-pms-payments-dialog",
    templateUrl: "./lean-pms-payments-dialog.component.html",
    styleUrls: ["./lean-pms-payments-dialog.component.scss"],
    standalone: true,
    imports: [
        BarcodeComponent,
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatTabsModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TilbyDialogProgressBarComponent,
        TranslateModule,
        MatIconModule,
        MatSelectModule,
        MatOptionModule,
        SharedModule,
        MatListModule,
        MatDividerModule,
    ]
})
export class LeanPmsPaymentsDialogComponent implements OnInit, OnDestroy {
    protected readonly data: LeanPmsPaymentsDialogData = inject(MAT_DIALOG_DATA);
    private readonly matDialogRef = inject(MatDialogRef);

    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly connectionService = inject(ConnectionService);
    private readonly leanPMSApi = inject(LeanPMSApiService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly translateService = inject(TranslateService);
    private readonly screenOrientationService = inject(ScreenOrientationService);
    
    private $$destroyed = false;
    private groupedReservations: LeanPMSGroupReservation[] = [];
    private notesCategory = this.configurationManagerService.getPreference('lean_pms.notes_category') || '-1';
    private paymentMethodId? = this.data.options?.paymentMethodId;
    private reservationId = this.data.options?.reservationId;
    private reservations: LeanPMSReservation[] = [];
    private txtQueryChanged: Subject<string> = new Subject<string>();

    protected readonly mode: 'services-view' | 'payment' = this.data.options?.amount == null ? 'services-view' : 'payment';
    protected readonly amountToPay = this.tilbyCurrencyPipe.transform(MathUtils.round(this.data.options?.amount || 0, 2));
    protected readonly enableConfirm = this.mode === 'payment' || !!this.data.options?.enableConfirm;
    protected readonly title = `CASHREGISTER.LEAN_PMS_PAYMENTS.TITLE`;

    protected fetchingReservations = false;
    protected hideRoomPrefix = !!this.configurationManagerService.getPreference('lean_pms.hide_room_prefix');
    protected hostname = '';
    protected hotelId?: number | undefined;
    protected hotels: LeanPMSHotel[] = [];
    protected isMobile = this.screenOrientationService.isMobilePortrait;
    protected isOffline = this.connectionService.isOffline;
    protected message = '';
    protected notesInProgress = false;
    protected operationInProgress = false;
    protected password = '';
    protected searchText = '';
    protected selectedReservation: LeanPMSReservation | LeanPMSGroupReservation | null = null;
    protected selectedReservationExtras: Array<LeanPMSReservationExtra> = [];
    protected selectedReservationNotes: Array<any> = [];
    protected status: 'login' | 'reservations-select' | 'groupedreservations-select' | 'hotel-select' | null = null;
    protected username = '';
    protected visibleGroupReservations: LeanPMSGroupReservation[] = [];
    protected visibleReservations: LeanPMSReservation[] = [];

    constructor() {
        this.txtQueryChanged.pipe(
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe((query: string) => {
            this.searchText = query;
            this.performSearch();
        });
    }

    onFieldSearchChange(query: string) {
        this.txtQueryChanged.next(query);
    }

    async ngOnInit() {
        this.hotelId = await this.leanPMSApi.getHotelId(this.paymentMethodId);

        if (this.hotelId) {
            await this.fetchReservations();
        } else {
            await this.fetchHotels();
        }
    }

    ngOnDestroy() {
        // Cleanup logic here
        this.$$destroyed = true;
        this.txtQueryChanged.complete();
        this.txtQueryChanged.unsubscribe();
    }

    parseLeanDate(date: string) {
        return this.tilbyDatePipe.transform(date, 'dd-MM-yyyy');
    }

    async performSearch(options?: { keyweftOnly?: boolean }) {
        if (!this.hotelId) {
            return;
        }

        this.operationInProgress = true;

        try {
            switch (this.status) {
                case 'reservations-select': {
                    this.visibleReservations = this.reservations;

                    //Try to search for keyweft first
                    const keyweftResult = this.visibleReservations.find(reservation => reservation.keyweft_set?.some(keyweft => keyweft.weft === this.searchText));

                    if (keyweftResult) {
                        this.visibleReservations = [keyweftResult];
                        this.selectReservation(keyweftResult);
                    }

                    if (keyweftResult || options?.keyweftOnly) {
                        return;
                    }

                    // No keyweft found, filter for other fields
                    const searchWords = this.searchText.split(' ');
                    const results: LeanPMSReservation[] = [];

                    for (const word of searchWords) {
                        for (const value of <(keyof LeanPMSReservation)[]>['room', 'contact_name', 'contact_surname']) {
                            const filterResult = this.visibleReservations.filter((reservation) => {
                                const targetValue = reservation[value];

                                return typeof targetValue === 'string' && targetValue?.toLowerCase().includes(word.toLowerCase())
                            });

                            results.push(...filterResult);
                        }
                    }

                    //Deduplicate results
                    this.visibleReservations = [...new Set(results)];

                    if (!this.visibleReservations.find((reservation) => reservation === this.selectedReservation)) {
                        this.deselectReservation();
                    }
                }
                    break;
                case 'groupedreservations-select': {
                    const groupId = parseInt(this.searchText);

                    if (!groupId || isNaN(groupId)) {
                        if (this.searchText) {
                            this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.NOT_FOUND_GROUPED_RESERVATIONS', { query: this.searchText });

                            asyncTimeout(3000).finally(() => {
                                this.searchText = '';
                                this.deleteMessage();
                            });
                        }

                        this.visibleGroupReservations = this.groupedReservations = [];
                    } else {
                        try {
                            const result = await this.leanPMSApi.getGroupReservations(this.hotelId, groupId, this.paymentMethodId);

                            this.visibleGroupReservations = this.groupedReservations = result.results;

                            if (!this.visibleGroupReservations.find((reservation) => reservation === this.selectedReservation)) {
                                this.deselectReservation();
                            }
                        } catch (error: any) {
                            this.handleFetchError(error);
                        }
                    }
                }
                    break;
                default:
                    break;
            }
        } finally {
            this.operationInProgress = false;
        }
    }

    async login() {
        this.operationInProgress = true;
        this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.ENABLING');

        try {
            await this.leanPMSApi.setupLogin(this.hostname, this.username, this.password, this.paymentMethodId);

            this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.ENABLE_SUCCESSFUL');
            asyncTimeout(1500).finally(() => this.deleteMessage());

            await this.fetchHotels();
        } catch (error: any) {
            switch (error?.status) {
                case -1:
                    this.message = '';
                    break;
                case 404: case 403: case 401:
                    this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.INVALID_CREDENTIALS');
                    break;
                default:
                    this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.ENABLE_FAILED');
                    break;
            }
        } finally {
            this.operationInProgress = false;
        }
    }

    async onBarcodeScanned(barcode: string) {
        this.searchText = barcode;
        this.performSearch({ keyweftOnly: true });
    }

    async pairHotel() {
        if (!this.hotelId || this.operationInProgress) {
            return;
        }

        this.operationInProgress = true;

        try {
            await this.leanPMSApi.setupHotel(this.hotelId, this.paymentMethodId);

            this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.ENABLE_SUCCESSFUL');
            asyncTimeout(1500).finally(() => this.deleteMessage());

            await this.fetchReservations();
        } catch (error: any) {
            switch (error?.status) {
                case -1:
                    this.message = '';
                    break;
                case 404: case 403: case 401:
                    this.status = 'login';
                    break;
                default:
                    this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.ENABLE_FAILED');
                    break;
            }
        } finally {
            this.operationInProgress = false;
        }
    }

    deleteMessage() {
        this.message = '';
    }

    onSelectionChange(event: MatSelectionListChange) {
        this.selectReservation(event.options[0]?.value as LeanPMSReservation | LeanPMSGroupReservation);
    }

    deselectReservation() {
        this.selectedReservation = null;
        this.selectedReservationNotes = [];
        this.selectedReservationExtras = [];
    }

    async selectReservation(reservation: LeanPMSReservation | LeanPMSGroupReservation) {
        this.deselectReservation();

        this.selectedReservation = reservation;

        if (reservation && 'reference' in reservation && this.hotelId) {
            if (reservation.extras) {
                this.selectedReservationExtras = reservation.extras.filter(service => this.isServiceAvailable(service));
            }

            // recupero le note
            try {
                this.notesInProgress = true;
                const reference = reservation.reference;
                const res = reference ? await this.leanPMSApi.getNotes(this.hotelId, reference, this.paymentMethodId) : null;

                if (res?.results?.length) {
                    const reservationNotes = res.results[0]?.notes;

                    // controllo se è settata una categoria
                    if(this.notesCategory === '-1') {
                        // nessuna categoria
                        this.selectedReservationNotes = reservationNotes || [];
                    } else {
                        // con categoria
                        const filterCategory = reservationNotes?.filter((note: any) => note?.categories[0]?.name === this.notesCategory);
                        this.selectedReservationNotes = filterCategory || [];
                    }
                }
            } catch (error: any) {
                this.log('ERR_NOTES', error);
            } finally {
                this.notesInProgress = false;
            }
        }
    }

    async changeReservationType(event: MatTabChangeEvent) {
        const type = event.index === 0 ? 'reservations' : 'groupedreservations';

        this.deselectReservation();
        this.status = `${type}-select`;
        this.searchText = '';
        await this.performSearch();

        switch (type) {
            case 'reservations':
                this.visibleReservations = this.reservations;
                break;
            case 'groupedreservations':
                this.visibleGroupReservations = this.groupedReservations;
                break;
        }
    }

    protected confirm() {
        if (!this.selectedReservation) {
            return;
        }

        switch (this.status) {
            case 'reservations-select':
                this.matDialogRef.close({
                    referenceData: {
                        reservation: (this.selectedReservation as LeanPMSReservation).reference,
                        hotel: this.hotelId
                    },
                    reservation: this.selectedReservation
                });

                break;
            case 'groupedreservations-select':
                this.matDialogRef.close({
                    referenceData: {
                        reservation_group: (this.selectedReservation as LeanPMSGroupReservation).id,
                        hotel: this.hotelId
                    },
                    reservation: this.selectedReservation
                });
                break;
        }
    }

    isServiceAvailable(service: LeanPMSReservationExtra) {
        return (service.day === this.tilbyDatePipe.transform(new Date(), 'yyyy-MM-dd'));
    }

    private handleFetchError(error: any) {
        switch (error?.status) {
            case -1:
                setTimeout(this.fetchReservations, 1000);
                break;
            case 401:
            case 403:
                this.status = 'login';
                break;
            default:
                if (error?.data?.error?.message?.error_code === 'CONNECTION_ERROR') {
                    this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.CHECK_CONNECTION');
                } else {
                    this.message = this.translateService.instant('CASHREGISTER.LEAN_PMS_PAYMENTS.UNKNOWN_ERROR');
                }
                break;
        }
    }

    private async fetchReservations() {
        if (this.$$destroyed || !this.hotelId) {
            return;
        }

        this.fetchingReservations = true;
        this.status = null;

        try {
            this.reservations = await this.leanPMSApi.getReservations(this.hotelId, undefined, this.paymentMethodId).then(d => d.results)
            this.status = 'reservations-select';
            this.visibleReservations = this.reservations;

            if(this.reservationId) {
                const targetReservation = this.reservations.find((reservation) => reservation.id === this.reservationId) || null;
                
                if(targetReservation) {
                    this.selectReservation(targetReservation);
                    this.onFieldSearchChange(targetReservation.room);
                }
            }
        } catch (error: any) {
            this.handleFetchError(error);
        } finally {
            this.fetchingReservations = false;
        }
    }

    private async fetchHotels() {
        try {
            this.hotels = await this.leanPMSApi.getHotels(this.paymentMethodId);
            this.status = 'hotel-select';
        } catch (error) {
            this.handleFetchError(error);
        }
    }

    private log(...args: any[]) {
        DevLogger.log('[LeanPMSPaymentsDialog]', ...args);
    }
}

@Injectable({
    providedIn: "root",
})
export class LeanPmsPaymentsDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(config?: NonNullableConfigData<LeanPmsPaymentsDialogData>) : Promise<LeanPMSPaymentsReturn> {
        const data: LeanPmsPaymentsDialogData = config?.data || {options: {}};
        const configEdited: NonNullableConfigData<LeanPmsPaymentsDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions({ width: '800px' }, { fullScreenForMobile: true }),
            disableClose: true,
            data,
        };
        return lastValueFrom(
            this.dialogRef.open(LeanPmsPaymentsDialogComponent, configEdited).afterClosed()
        );
    }
}
